import { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { ToastContainer } from "react-toastify"; // ⬅️ switched to react-toastify
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CustomDatePicker from "../../Common/DatePicker";
import Select from "react-select";
import { MultiSelect } from "react-multi-select-component";
import {
  autoScrollSelectComponents,
  scrollMultiSelectToSelectionOnOpen,
} from "../../Common/SelectMenuScroll";
import { capitalizeFirstLetter } from "../../../utilities/CommonUtilities";
import { buildCombinedSelectStyles } from "../../CoTrader/CoTraderCombinedFilter";

// Same select look as the CoTrader selection bar.
const defaultSelectStyles = buildCombinedSelectStyles();
const compactSelectStyles = buildCombinedSelectStyles({ compact: true });

const FilterField = ({
  children,
  fieldRef,
  grow = 1,
  label,
  minWidth,
  primary = false,
}) => (
  <div
    ref={fieldRef}
    className={`pd-filter-field${primary ? " pd-filter-field--primary" : ""}`}
    style={{ flex: `${grow} 1 0`, minWidth }}
  >
    <div className="pd-filter-label">{label}</div>
    {children}
  </div>
);

const FilterBar = forwardRef(
  (
    {
      filters,
      filterDate,
      setFilterDate,
      selectedFilters,
      setSelectedFilters,
      isLoading,
      dashboardChartData,
      dashboard_name,
      start_date_limit,
      end_date_limit,
      setDashboardGranularity,
      dashboardGranularity,
      availableGranularities,
      topRightContent = null,
    },
    ref
  ) => {
    const MAX_FILTER3 = 30; // ⬅️ hard cap
    const filter3MenuRef = useRef(null);
    const filter1Values = useMemo(() => {
      if (filters) {
        return Object.keys(filters).map((x) => ({
          value: x.toUpperCase(),
          label: x
            .split("_")
            .map(
              (word) =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            )
            .join(" "),
        }));
      }
      return [];
    }, [filters]);
    const filter2Values = useMemo(() => {
      if (
        filters &&
        selectedFilters["filter1"]?.value.toLowerCase() &&
        filters[selectedFilters["filter1"]?.value.toLowerCase()]
      ) {
        return Object.keys(
          filters[selectedFilters["filter1"].value.toLowerCase()]
        )
          .sort()
          .map((x) => ({
            value: x,
            label: x
              .split(" ")
              .map(
                (word) =>
                  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
              )
              .join(" "),
          }));
      }
      return [];
    }, [filters, selectedFilters["filter1"]]);
    const filter3Values = useMemo(() => {
      if (
        filters &&
        selectedFilters["filter1"] &&
        selectedFilters["filter2"] &&
        selectedFilters["filter2"].length > 0 &&
        filters[selectedFilters["filter1"].value.toLowerCase()] &&
        filters[selectedFilters["filter1"].value.toLowerCase()][
          selectedFilters["filter2"][0].value
        ]
      ) {
        var values = filters[selectedFilters["filter1"].value.toLowerCase()][
          selectedFilters["filter2"][0].value
        ]
          .sort()
          .map((x) => ({
            value: x,
            label: x,
          }));

        if (
          values.length > MAX_FILTER3 &&
          selectedFilters["filter3"].length === 0
        ) {
          setSelectedFilters({
            ...selectedFilters,
            filter3: values.slice(0, 1),
          });
        }
        return values;
      }
      return [];
    }, [filters, selectedFilters["filter1"], selectedFilters["filter2"]]);
    const granularityOptions = useMemo(
      () =>
        availableGranularities.map((x) => ({
          value: x,
          label: x,
        })),
      [availableGranularities],
    );
    const handleFilter1Change = (value) => {
      setSelectedFilters({
        ...selectedFilters,
        filter1: value,
      });
    };

    const filter1Key = selectedFilters.filter1?.value;

    useImperativeHandle(ref, () => ({
      handleSentimentClick: (company, country) => {
        if (!filter1Key || !filters?.[filter1Key?.toLowerCase()]) return;
        const matchedCountryKey = Object.keys(filters[filter1Key?.toLowerCase()]).find(
          (key) => key.toLowerCase() === country.toLowerCase()
        );
        if (!matchedCountryKey) return;
        
        const matchedCountry = {
          value: matchedCountryKey,
          label: matchedCountryKey
            .split(" ")
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" "),
        };
        const companies = filters[filter1Key?.toLowerCase()][matchedCountryKey] || {};

        const matchedCompanyKey = companies.find(
          (key) =>
            key.toLowerCase() === company.toLowerCase() ||
            key.toLowerCase().includes(company.toLowerCase())
        );

        if (!matchedCompanyKey) return;

        const matchedCompany = {
          value: matchedCompanyKey,
          label: matchedCompanyKey
            .split(" ")
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" "),
        };
        setSelectedFilters((prev) => ({
          ...prev,
          filter2: [matchedCountry],
          filter3: [matchedCompany],
        }));
      },
    }));

    const customValueRenderer = (selected, _options) => {
      const isCompany = dashboard_name?.toLowerCase().includes("company");
      const isGeo =
        dashboard_name?.toLowerCase().includes("macro") ||
        dashboard_name?.toLowerCase().includes("frontier");
      if (_options.length === 0) {
        return "No options available";
      } else if (selected.length === _options.length) {
        if (isCompany) return "All Companies";
        if (isGeo) return "All Themes";
        return "All Themes";
      } else if (selected.length === 0) {
        if (isCompany) return "All Companies";
        if (isGeo) return "All Themes";
        return "All Themes";
      } else if (selected.length === 1) {
        return selected[0].label;
      } else {
        return selected.map(({ label }) => label).join(", ");
      }
    };

    const handleFilter2Change = (value) => {
      if (value.length === 0 && filter2Values.length > 0) {
        setSelectedFilters({
          ...selectedFilters,
          filter2: [filter2Values[0]],
          filter3: [],
        });
      } else {
        setSelectedFilters({
          ...selectedFilters,
          filter2: value,
          filter3: [],
        });
      }
    };

    const handleFilter3Change = (value) => {
      // Dedup by value (MultiSelect can sometimes pass duplicates on fast clicks)
      const dedup = Array.from(
        new Map(value.map((v) => [v.value, v])).values()
      );

      if (dedup.length === 0 && filter3Values.length > 0) {
        setSelectedFilters({
          ...selectedFilters,
          filter3: [filter3Values[0]],
        });
        return;
      }

      if (dedup.length > MAX_FILTER3) {
        toast.error(`You can select up to ${MAX_FILTER3} selections.`);
        setSelectedFilters({
          ...selectedFilters,
          filter3: dedup.slice(0, MAX_FILTER3), // keep first 15 selected
        });
        return;
      }

      setSelectedFilters({
        ...selectedFilters,
        filter3: dedup,
      });
    };

    const handleGranularityChange = (value) => {
      setDashboardGranularity(value);
    };

    // Fields stay mounted while chart data loads (disabled) so the row never reflows.
    const isFilterDisabled = isLoading || dashboardChartData === null;

    const filter3Label = dashboard_name?.toLowerCase().includes("company")
      ? "Companies"
      : "Themes";

    return (
      <div className="pd-filters">
        <ToastContainer /> {/* react-toastify container */}
        <div className="pd-filters-row">
          <div className="pd-filters-grid">
            <FilterField label="Date Range" grow={0} minWidth={292}>
              <CustomDatePicker
                className="pd-date-range"
                dateFormat="MMM d, yyyy"
                showIcon
                setDateFilter={setFilterDate}
                filterDate={filterDate}
                isLoading={isLoading}
                start_date_limit={start_date_limit}
                end_date_limit={end_date_limit}
                key={`date-${dashboard_name}`}
              />
            </FilterField>

            <FilterField label="Country" grow={0.95} minWidth={180}>
              <Select
                options={filter2Values}
                value={selectedFilters["filter2"]?.[0] || null}
                onChange={(value) => handleFilter2Change([value])}
                aria-label="Country"
                placeholder="Country"
                isDisabled={isFilterDisabled}
                menuPortalTarget={document.body}
                styles={defaultSelectStyles}
                components={autoScrollSelectComponents}
              />
            </FilterField>

            {filter1Values.length > 1 && (
              <FilterField label="Category" grow={0.85} minWidth={170}>
                <Select
                  value={capitalizeFirstLetter(selectedFilters["filter1"])}
                  options={filter1Values}
                  onChange={handleFilter1Change}
                  isSearchable={true}
                  name="category"
                  placeholder="Select Category"
                  aria-label="Category"
                  isDisabled={isFilterDisabled}
                  menuPortalTarget={document.body}
                  styles={defaultSelectStyles}
                  components={autoScrollSelectComponents}
                />
              </FilterField>
            )}

            <FilterField
              label={filter3Label}
              grow={1.3}
              minWidth={220}
              primary
              fieldRef={filter3MenuRef}
            >
              <MultiSelect
                options={filter3Values}
                value={
                  selectedFilters["filter3"].map((x) => {
                    return capitalizeFirstLetter(x);
                  }) || []
                }
                onChange={handleFilter3Change} // ⬅️ capped at 15
                labelledBy={filter3Label}
                className="multi-select pd-multi-select"
                disabled={isFilterDisabled}
                valueRenderer={customValueRenderer}
                hasSelectAll={filter3Values.length < MAX_FILTER3}
                onMenuToggle={scrollMultiSelectToSelectionOnOpen(filter3MenuRef)}
              />
            </FilterField>

            {availableGranularities.length > 1 && (
              <FilterField label="Granularity" grow={0.8} minWidth={150}>
                <Select
                  value={dashboardGranularity}
                  options={granularityOptions}
                  onChange={handleGranularityChange}
                  isSearchable={true}
                  name="granularity"
                  placeholder="Granularity"
                  aria-label="Granularity"
                  isDisabled={isFilterDisabled}
                  menuPortalTarget={document.body}
                  styles={compactSelectStyles}
                  components={autoScrollSelectComponents}
                />
              </FilterField>
            )}
          </div>

          {topRightContent ? (
            <div className="pd-filters-actions">{topRightContent}</div>
          ) : null}
        </div>
      </div>
    );
  }
);

export default FilterBar;
